import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { getAuthMembers, login } from '../lib/api';
import { setSession } from '../lib/session';
import { ArrowRight } from '@phosphor-icons/react';

export default function LoginScreen({ onLogin }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const loadMembers = useCallback(async () => {
    try { setMembers(await getAuthMembers()); } catch (e) { logError('Load members error:', e); }
  }, []);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleDigit = (d) => {
    if (passcode.length < 4) setPasscode(p => p + d);
    setError('');
  };
  const handleDelete = () => setPasscode(p => p.slice(0, -1));

  const submitRef = useRef(null);

  const handleSubmit = useCallback(async () => {
    if (!selected || passcode.length !== 4) return;
    setLoading(true); setError('');
    try {
      const result = await login(selected.id, passcode);
      setSession(result.token, result.user);
      onLogin(result.user);
    } catch (e) {
      setError(e.response?.data?.detail || 'Login failed');
      setPasscode('');
    } finally { setLoading(false); }
  }, [selected, passcode, onLogin]);

  useEffect(() => { submitRef.current = handleSubmit; }, [handleSubmit]);

  // Keyboard support for passcode entry
  useEffect(() => {
    if (!selected) return;
    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') { handleDigit(e.key); }
      else if (e.key === 'Backspace') { handleDelete(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selected, passcode.length]);
  useEffect(() => { if (passcode.length === 4 && selected) submitRef.current?.(); }, [passcode, selected]);

  const ROLE_COLORS = {
    Admin: 'chip-warn',
    SA: 'chip-info',
    MC: 'chip-ok',
    AVP: 'chip-info',
    LC: 'chip-ok',
    PM: 'chip-critical',
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-3 sm:p-4" data-testid="login-screen">
      <div className="w-full max-w-md">
        {/* Compact header — fits on 667px phones with zero scroll */}
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-2.5 mb-2">
            <img src="/icons/mr-daily-tile.png" alt="MR Daily" className="w-12 h-12 rounded-md border border-border bg-white shadow-sm" />
            <div className="text-left">
              <div className="text-sm font-extrabold text-foreground leading-tight">MR Daily</div>
              <div className="mr-eyebrow mt-0.5" style={{ fontSize: '9px' }}>Your priorities. Your next move.</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Secure sign-in for Team Hamilton</p>
        </div>

        {!selected ? (
          /* Member Selection — tight rows */
          <div data-testid="member-select">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 text-center">Who's signing in?</p>
            <div className="space-y-1.5">
              {members.map(m => (
                <button key={m.id} onClick={() => { setSelected(m); setPasscode(''); setError(''); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-card hover:border-primary/60 hover:bg-muted/50 transition-all text-left"
                  data-testid={`member-btn-${m.initials.toLowerCase()}`}>
                  <span className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">{m.initials}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-tight truncate">{m.name}</p>
                    <span className={`inline-block text-[9px] px-1.5 py-0.5 mt-0.5 rounded-full border font-semibold uppercase tracking-wider ${ROLE_COLORS[m.role] || 'chip-neutral'}`}>{m.role}</span>
                  </div>
                  <ArrowRight size={16} className="text-muted-foreground flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Passcode Entry — compact */
          <div data-testid="passcode-entry">
            <button onClick={() => { setSelected(null); setPasscode(''); setError(''); }}
              className="text-xs text-muted-foreground hover:text-foreground mb-2 flex items-center gap-1">
              ← Back to team
            </button>
            <Card className="border border-border bg-card">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2.5 mb-3">
                  <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-base font-bold">{selected.initials}</div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-foreground leading-tight">{selected.name}</p>
                    <p className="text-[11px] text-muted-foreground">Enter 4-digit passcode</p>
                  </div>
                </div>

                {/* Dots */}
                <div className="flex justify-center gap-3 mb-3">
                  {[0,1,2,3].map(i => (
                    <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${
                      i < passcode.length ? 'bg-primary border-primary scale-110' : 'border-border'
                    }`} />
                  ))}
                </div>

                {error && (
                  <div className="chip-critical border rounded-md p-2 mb-2" data-testid="login-error">
                    <p className="text-xs font-semibold text-center">{error}</p>
                  </div>
                )}

                {/* Keypad — tighter */}
                <div className="grid grid-cols-3 gap-2 max-w-[220px] mx-auto">
                  {[1,2,3,4,5,6,7,8,9].map(d => (
                    <button key={d} onClick={() => handleDigit(String(d))}
                      className="h-11 rounded-lg bg-muted hover:bg-muted/80 text-lg font-semibold text-foreground transition-colors active:scale-95"
                      data-testid={`key-${d}`}>{d}</button>
                  ))}
                  <button onClick={handleDelete} className="h-11 rounded-lg bg-muted hover:bg-muted/80 text-sm font-medium text-muted-foreground transition-colors" data-testid="key-delete">Del</button>
                  <button onClick={() => handleDigit('0')} className="h-11 rounded-lg bg-muted hover:bg-muted/80 text-lg font-semibold text-foreground transition-colors" data-testid="key-0">0</button>
                  <div />
                </div>

                {loading && <div className="mt-2"><div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent mx-auto" /></div>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
